import { useMemo } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { usePermissions } from '@practium/features/permissions';
import { useGetGroup, useGetHierarchy, useGetManagementPlan } from '@practium/features/api';
import { AddSystemPage } from './AddSystemPage';
import { mapGroupDTO, mapHierarchyDTO, mapManagementPlanDTO } from '../utils/systemMappers';
import type { SystemTab, GroupFormData, HierarchyFormData, PlanFormData, SystemSubject } from '../types/system.types';

const resolveId = (val: string | { _id: string } | null | undefined): string => {
	if (!val) return '';
	return typeof val === 'string' ? val : val._id;
};

const TAB_SUBJECT: Record<SystemTab, SystemSubject> = {
	permissions: 'groups',
	hierarchies: 'hierarchies',
	plans: 'plans',
};

const toGroupFormData = (groupDTO: Parameters<typeof mapGroupDTO>[0]): GroupFormData => {
	const g = mapGroupDTO(groupDTO);
	return { name: g.name, permissions: g.permissions };
};

const toHierarchyFormData = (hierarchyDTO: Parameters<typeof mapHierarchyDTO>[0]): HierarchyFormData => {
	const h = mapHierarchyDTO(hierarchyDTO);
	return {
		name: h.name,
		description: h.description ?? '',
		farmer: resolveId(h.farmer.group),
		consultant: resolveId(h.consultant.group),
		company: resolveId(h.company.group),
		master: resolveId(h.master.group),
	};
};

const toPlanFormData = (planDTO: Parameters<typeof mapManagementPlanDTO>[0]): PlanFormData => {
	const p = mapManagementPlanDTO(planDTO);
	return {
		name: p.name,
		description: p.description ?? '',
		paymentMethods: p.paymentMethods,
		hierarchy: resolveId(p.hierarchy),
		isListable: p.isListable ? 'yes' : 'no',
		price: p.price ? (p.price / 100).toFixed(2).replace('.', ',') : '',
		recurrentPayment: p.recurrentPayment ? 'yes' : 'no',
		interval: p.interval,
		intervalCount: String(p.intervalCount ?? ''),
		gracePeriod: p.gracePeriod == null ? '' : String(p.gracePeriod),
		notifyUnpayment: p.notifyUnpayment ? 'yes' : 'no',
		notifyInvoice: p.notifyInvoice ? 'yes' : 'no',
		maxInstallments: String(p.maxInstallments ?? ''),
		maxProps: String(p.maxProps ?? ''),
		maxSamples: String(p.maxSamples ?? ''),
		monthlyFloraTokens: p.monthlyFloraTokens ?? '',
		paymentLink: p.paymentLink ?? '',
		advantages: p.advantages?.length ? p.advantages : [''],
	};
};

export const ViewSystemPageAdmin = () => {
	const navigate = useNavigate();
	const [searchParams] = useSearchParams();
	const type = (searchParams.get('type') as SystemTab) || 'permissions';
	const id = searchParams.get('id') || '';

	const { abilities } = usePermissions();
	const subject = TAB_SUBJECT[type];
	const canEdit = abilities.can('update', subject);
	const canDelete = abilities.can('delete', subject);

	const { data: groupDTO } = useGetGroup(id, { enabled: type === 'permissions' });
	const { data: hierarchyDTO } = useGetHierarchy(id, { enabled: type === 'hierarchies' });
	const { data: planDTO } = useGetManagementPlan(id, { enabled: type === 'plans' });

	const initialData = useMemo<GroupFormData | HierarchyFormData | PlanFormData | null>(() => {
		if (type === 'permissions') return groupDTO ? toGroupFormData(groupDTO) : null;
		if (type === 'hierarchies') return hierarchyDTO ? toHierarchyFormData(hierarchyDTO) : null;
		return planDTO ? toPlanFormData(planDTO) : null;
	}, [type, groupDTO, hierarchyDTO, planDTO]);

	if (!initialData) return null;

	return (
		<AddSystemPage
			id={id}
			type={type}
			onBack={() => navigate(`/sistema?tab=${type}`)}
			initialMode="view"
			initialData={initialData}
			canEdit={canEdit}
			onDelete={canDelete ? () => navigate(`/sistema?tab=${type}`) : undefined}
		/>
	);
};
